import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Box, Grid, Typography, Button, Skeleton } from '@mui/material';
import {
  CheckCircleOutline, ErrorOutline, HourglassEmpty, ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Footer from '../../components/Footer/Footer';
import Panel from '../../components/Reports/Panel';
import StatTile from '../../components/Reports/StatTile';
import BarChart from '../../components/Reports/BarChart';
import LineChart from '../../components/Reports/LineChart';
import { statusBars } from '../../utils/supplierTiles';
import { PIPELINE_RAMP, SERIES, STATUS, INK, FONT } from '../../components/Reports/vizTokens';

const EMPTY_SUMMARY = {
  total: 0, byStatus: [], byDepartment: [], pipeline: [], outcomes: [],
  exception: null, byDepartmentStage: [], byAge: [], oldestWaitingDays: 0,
};

const ACTION_LABEL = {
  approved: 'approved',
  rejected: 'rejected',
  requested_info: 'sent back for more information',
  submitted: 'submitted',
};

const relativeTime = (value) => {
  if (!value) return '';
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? 'last week' : `${weeks} weeks ago`;
};

// "2026-09-07" -> "7 Sep". The axis labels roughly six of thirty points, so
// these have room; the rest are carried by the hover tooltip.
const dayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${d.getUTCDate()} ${d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })}`;
};

/**
 * The landing page for internal users who have no task queue of their own.
 *
 * Every number here comes from the same summarise() the report uses, and every
 * chart from the same components, so the two pages cannot disagree — not by
 * discipline, but because there is only one implementation of each.
 *
 * There is no table and no export: this page is a glance, and every panel links
 * into the report, which is the workbench.
 */
const Overview = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [throughput, setThroughput] = useState([]);
  const [activity, setActivity] = useState([]);
  const [queue, setQueue] = useState({ assignedToMe: 0, unclaimed: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      // limit=1 because this page wants the summary, not the rows. Pulling 25
      // rows to throw them away is the kind of waste that stays invisible until
      // the table is large.
      const [report, tp, act, q] = await Promise.all([
        api.get('/reports/suppliers', { params: { limit: 1 } }),
        api.get('/dashboard/throughput', { params: { days: 30 } }),
        api.get('/dashboard/recent-activities', { params: { limit: 8 } }),
        api.get('/dashboard/my-queue'),
      ]);
      setSummary({ ...EMPTY_SUMMARY, ...(report.data.summary || {}) });
      setThroughput(tp.data?.data?.days || []);
      setActivity(act.data?.data || []);
      setQueue(q.data?.data || { assignedToMe: 0, unclaimed: 0 });
    } catch (error) {
      // A failed panel must not blank the page; the empty states below carry it.
      console.error('Overview load failed', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inFlight = useMemo(
    () => (summary.pipeline || []).filter((s) => s.key !== 'draft').reduce((n, s) => n + s.count, 0)
      + (summary.exception?.count || 0),
    [summary]
  );
  const overdue = useMemo(
    () => (summary.byAge || []).find((b) => b.key === '30_plus')?.count || 0,
    [summary]
  );
  // Completed only: 'approved' is an old holding status still waiting on a
  // vendor number, and the report now shows it in the pipeline.
  const approved = useMemo(
    () => (summary.outcomes || []).find((o) => o.key === 'completed')?.count || 0,
    [summary]
  );

  const departmentRows = useMemo(
    () => (summary.byDepartment || []).slice(0, 6).map((r) => ({
      key: r.department === 'Unassigned' ? '__unassigned' : r.department,
      label: r.department,
      count: r.count,
    })),
    [summary]
  );

  const throughputPoints = useMemo(
    () => throughput.map((d) => ({ label: dayLabel(d.date), a: d.submitted, b: d.approved })),
    [throughput]
  );

  const toReport = (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    navigate(qs ? `/reports?${qs}` : '/reports');
  };

  const block = (h) => <Skeleton variant="rounded" height={h} sx={{ borderRadius: '10px' }} />;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, sm: 5 }, pb: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 }, flex: 1 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ ...FONT, fontWeight: 'bold', mb: 1, color: INK.primary, fontSize: { xs: '22px', sm: '28px' } }}>
            Overview
          </Typography>
          <Typography sx={{ ...FONT, color: INK.secondary, fontSize: '14px' }}>
            How supplier onboarding is going right now. Every panel opens the full report.
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            {loading ? block(122) : (
              <StatTile hero label="Suppliers" value={summary.total} note="all applications"
                onClick={() => toReport()} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {loading ? block(122) : (
              <StatTile label="In flight" value={inFlight} note="awaiting a decision from us"
                icon={<HourglassEmpty />} onClick={() => toReport({ stage: 'procurement' })} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {loading ? block(122) : (
              <StatTile
                label="Waiting over 30 days"
                value={overdue}
                accent={overdue > 0 ? STATUS.critical : undefined}
                noteTone={overdue > 0 ? STATUS.critical : undefined}
                icon={overdue > 0 ? <ErrorOutline /> : undefined}
                note={summary.oldestWaitingDays ? `longest wait ${summary.oldestWaitingDays} days` : 'nothing is overdue'}
                onClick={() => toReport()}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {loading ? block(122) : (
              <StatTile label="Completed" value={approved} accent={STATUS.good}
                icon={<CheckCircleOutline />}
                note={summary.total ? `${Math.round((approved / summary.total) * 100)}% of total` : undefined}
                onClick={() => toReport({ stage: 'completed' })} />
            )}
          </Grid>
        </Grid>

        <Box sx={{ mb: 2.5 }}>
          {loading ? block(280) : (
            <Panel
              title="Where applications are sitting"
              subtitle="In workflow order, not by size — so a queue backing up shows as a shape."
            >
              <BarChart
                rows={statusBars(summary)}
                colors={PIPELINE_RAMP}
                onSelect={(row) => toReport({ stage: row.key })}
                emptyText="No applications yet"
              />
            </Panel>
          )}
        </Box>

        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={7}>
            {loading ? block(300) : (
              <Panel
                alignSubtitle
                title="Throughput"
                subtitle="Applications submitted against applications approved, per day, over the last thirty."
              >
                <LineChart points={throughputPoints} seriesA="Submitted" seriesB="Approved" />
              </Panel>
            )}
          </Grid>
          <Grid item xs={12} md={5}>
            {loading ? block(300) : (
              <Panel
                alignSubtitle
                title="Unclaimed work"
                subtitle="Applications in flight that nobody has picked up yet."
              >
                {/* No "assigned to you" tile: this page is for roles that own no
                    personal queue, so the number was always zero and it linked to
                    a view they are not meant to have. */}
                {/* Not stretched to the neighbouring panel's height: a lone
                    tile grown to match a 300px chart is mostly empty box. */}
                <Box sx={{ '& > *': { height: 'auto' } }}>
                <StatTile
                  label="Waiting to be picked up"
                  value={queue.unclaimed}
                  accent={queue.unclaimed > 0 ? STATUS.warning : undefined}
                  icon={queue.unclaimed > 0 ? <ErrorOutline /> : undefined}
                  note={queue.unclaimed > 0 ? 'open All Tasks to assign them' : 'everything in flight is owned'}
                  onClick={() => navigate('/tasks/all')}
                />
                </Box>
              </Panel>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            {loading ? block(280) : (
              <Panel alignSubtitle title="By department" subtitle="Who owns the work. Unassigned is work nobody has picked up.">
                <BarChart
                  rows={departmentRows}
                  colors={SERIES}
                  onSelect={(row) => toReport({ department: row.key })}
                  emptyText="No departments to show"
                />
              </Panel>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {loading ? block(280) : (
              <Panel alignSubtitle title="Recent activity" subtitle="The last decisions taken, newest first.">
                {activity.length === 0 ? (
                  <Typography sx={{ ...FONT, color: INK.muted, fontSize: '13px', py: 2 }}>
                    Nothing has been decided yet
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {activity.map((row, i) => (
                      <Box
                        key={`${row.supplierId}-${row.timestamp}-${i}`}
                        onClick={() => navigate(`/suppliers/${row.supplierId}`)}
                        sx={{
                          display: 'flex', justifyContent: 'space-between', gap: 1.5,
                          cursor: 'pointer', borderRadius: '6px', px: 1, py: 0.5, mx: -1,
                          '&:hover': { backgroundColor: INK.plane },
                        }}
                      >
                        <Typography sx={{ ...FONT, fontSize: '13px', color: INK.secondary, lineHeight: 1.45 }}>
                          <strong style={{ color: INK.primary, fontWeight: 600 }}>{row.supplierName}</strong>
                          {' '}{ACTION_LABEL[row.action] || row.action}
                          {row.actorName ? ` by ${row.actorName}` : ''}
                        </Typography>
                        <Typography sx={{ ...FONT, fontSize: '12px', color: INK.muted, whiteSpace: 'nowrap' }}>
                          {relativeTime(row.timestamp)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Panel>
            )}
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            endIcon={<ArrowForward />}
            onClick={() => toReport()}
            sx={{ ...FONT, textTransform: 'none', fontSize: '14px', color: '#374151' }}
          >
            View the full report
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
};

export default Overview;
